/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React, { Component } from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { FixedSizeList } from 'react-window';
import { EuiFlexGroup, EuiFlexItem } from '../../flex';
import { EuiHighlight } from '../../highlight';
import { EuiMark } from '../../mark';
import { EuiText } from '../../text';
import { EuiLoadingSpinner } from '../../loading';
import { EuiComboBoxTitle } from './combo_box_title';
import { EuiI18n } from '../../i18n';
import { EuiFilterSelectItem } from '../../filter_group/filter_select_item';
import { EuiBadge } from '../../badge';
import { EuiPopoverPanel } from '../../popover/popover_panel';
import { EuiTextTruncate } from '../../text_truncate';
var hitEnterBadge = ___EmotionJSX(EuiBadge, {
  className: "euiComboBoxOption__enterBadge",
  color: "hollow",
  iconType: "returnKey",
  "aria-hidden": "true"
});
export class EuiComboBoxOptionsList extends Component {
  listRefInstance = null;
  listRef = null;
  listBoxRef = null;
  static defaultProps = {
    'data-test-subj': '',
    rowHeight: 29,
    // row height of default option renderer
    isCaseSensitive: false
  };
  updatePosition = () => {
    // Wait a beat for the DOM to update, since we depend on DOM elements' bounds.
    requestAnimationFrame(() => {
      this.props.updatePosition(this.listRefInstance);
    });
  };
  componentDidMount() {
    // Wait a frame, otherwise moving focus from one combo box to another will result in the class
    // being removed from the body.
    requestAnimationFrame(() => {
      document.body.classList.add('euiBody-hasPortalContent');
    });
    this.updatePosition();
    window.addEventListener('resize', this.updatePosition);

    // Firefox will trigger a scroll event in many common situations when the options list div is appended
    // to the DOM; in testing it was always within 100ms, but setting a timeout here for 500ms to be safe
    setTimeout(() => {
      window.addEventListener('scroll', this.closeListOnScroll, {
        passive: true,
        // for better performance as we won't call preventDefault
        capture: true // scroll events don't bubble, they must be captured instead
      });
    }, 500);
  }
  componentDidUpdate(prevProps) {
    const {
      options,
      selectedOptions,
      searchValue
    } = prevProps;

    // We don't compare matchingOptions because that will result in a loop.
    if (searchValue !== this.props.searchValue || options !== this.props.options || selectedOptions !== this.props.selectedOptions) {
      this.updatePosition();
    }
    if (this.listRef && typeof this.props.activeOptionIndex !== 'undefined' && this.props.activeOptionIndex !== prevProps.activeOptionIndex) {
      this.listRef.scrollToItem(this.props.activeOptionIndex, 'auto');
    }
  }
  componentWillUnmount() {
    document.body.classList.remove('euiBody-hasPortalContent');
    window.removeEventListener('resize', this.updatePosition);
    window.removeEventListener('scroll', this.closeListOnScroll, {
      capture: true
    });
  }
  closeListOnScroll = event => {
    // Close the list when a scroll event happens, but not if the scroll happened in the options list.
    // This mirrors Firefox's approach of auto-closing `select` elements onscroll.
    if (this.listRefInstance && event.target && this.listRefInstance.contains(event.target) === false) {
      this.props.onCloseList(event);
    }
  };
  listRefCallback = ref => {
    this.props.listRef(ref);
    this.listRefInstance = ref;
  };
  setListRef = ref => {
    this.listRef = ref;
  };
  setListBoxRef = ref => {
    this.listBoxRef = ref;
    if (ref) {
      ref.setAttribute('aria-label', this.props.listboxAriaLabel);
      ref.setAttribute('id', this.props.rootId('listbox'));
      ref.setAttribute('role', 'listbox');
      ref.setAttribute('tabIndex', '0');
    }
  };
  ListRow = ({
    data,
    index,
    style
  }) => {
    const option = data[index];
    const {
      key,
      isGroupLabelOption,
      label,
      value,
      prepend,
      append,
      truncationProps: _truncationProps,
      ...rest
    } = option;
    const {
      singleSelection,
      selectedOptions,
      onOptionClick,
      optionRef,
      activeOptionIndex,
      renderOption,
      searchValue,
      rootId
    } = this.props;
    const hasTruncationProps = this.props.truncationProps || _truncationProps;
    const truncationProps = hasTruncationProps ?
    // Individual truncation settings should override component prop
    {
      ...this.props.truncationProps,
      ..._truncationProps
    } : undefined;
    if (isGroupLabelOption) {
      return <div key={key ?? label} style={style}>
          <EuiComboBoxTitle>{label}</EuiComboBoxTitle>
        </div>;
    }
    let checked = undefined;
    if (singleSelection && selectedOptions.length && selectedOptions[0].label === label && selectedOptions[0].key === key) {
      checked = 'on';
    }
    const optionIsFocused = activeOptionIndex === index;
    const optionIsDisabled = option.hasOwnProperty('disabled') && option.disabled === true;
    return <EuiFilterSelectItem style={style} key={option.key ?? option.label} onClick={() => {
      if (onOptionClick) {
        onOptionClick(option);
      }
    }} ref={optionRef.bind(this, index)} isFocused={optionIsFocused} checked={checked} showIcons={singleSelection ? true : false} id={rootId(`_option-${index}`)} title={label} {...rest}>
        <span className="euiComboBoxOption__contentWrapper">
          {prepend && <span className="euiComboBoxOption__prepend">{prepend}</span>}
          <span className="euiComboBoxOption__content">
            {renderOption ? renderOption(option, searchValue, 'euiComboBoxOption__renderOption') : this.renderTruncatedOption(label, truncationProps)}
          </span>
          {append && <span className="euiComboBoxOption__append">{append}</span>}
          {optionIsFocused && !optionIsDisabled ? hitEnterBadge : null}
        </span>
      </EuiFilterSelectItem>;
  };
  optionWidth;
  setOptionWidth = width => {
    this.optionWidth = width;
  };
  renderTruncatedOption = (text, truncationProps) => {
    const searchValue = this.props.searchValue.trim();
    if (!truncationProps && !searchValue) {
      // Default to CSS text-overflow
      return text;
    }
    if (!searchValue) {
      return <EuiTextTruncate width={this.optionWidth} onResize={this.setOptionWidth} {...truncationProps} text={text}>
          {text => text}
        </EuiTextTruncate>;
    }
    const searchPositionStart = this.props.isCaseSensitive ? text.indexOf(searchValue) : text.toLowerCase().indexOf(searchValue.toLowerCase());
    const searchPositionCenter = searchPositionStart + Math.floor(searchValue.length / 2);
    return <EuiTextTruncate width={this.optionWidth} onResize={this.setOptionWidth} {...truncationProps}
    // When searching, don't allow overriding the truncation settings
    truncation="startEnd" truncationPosition={searchPositionCenter} text={text}>
        {text => <>
            {text.length >= searchValue.length ? <EuiHighlight search={searchValue} strict={this.props.isCaseSensitive}>
                {text}
              </EuiHighlight> :
        // If the available truncated text is shorter than the full search string,
        // just highlight the entire truncated text
        <EuiMark>{text}</EuiMark>}
          </>}
      </EuiTextTruncate>;
  };
  render() {
    const {
      'data-test-subj': dataTestSubj,
      activeOptionIndex,
      areAllOptionsSelected,
      customOptionText,
      fullWidth,
      getSelectedOptionForSearchValue,
      isCaseSensitive,
      isLoading,
      listRef,
      matchingOptions,
      onCloseList,
      onCreateOption,
      onOptionClick,
      onOptionEnterKey,
      onScroll,
      optionRef,
      options,
      position = 'bottom',
      renderOption,
      rootId,
      rowHeight,
      scrollToIndex,
      searchValue,
      selectedOptions,
      singleSelection,
      updatePosition,
      width,
      delimiter,
      zIndex,
      style,
      truncationProps,
      listboxAriaLabel,
      ...rest
    } = this.props;
    let emptyStateContent;
    if (isLoading) {
      emptyStateContent = <EuiFlexGroup gutterSize="s" justifyContent="center">
          <EuiFlexItem grow={false}>
            <EuiLoadingSpinner size="m" />
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiI18n token="euiComboBoxOptionsList.loadingOptions" default="Loading options" />
          </EuiFlexItem>
        </EuiFlexGroup>;
    } else if (searchValue && matchingOptions && matchingOptions.length === 0) {
      if (onCreateOption && getSelectedOptionForSearchValue) {
        if (delimiter && searchValue.includes(delimiter)) {
          emptyStateContent = <div className="euiComboBoxOption__contentWrapper">
              <p className="euiComboBoxOption__emptyStateText">
                <EuiI18n token="euiComboBoxOptionsList.delimiterMessage" default="Add each item separated by {delimiter}" values={{
                delimiter: <strong>{delimiter}</strong>
              }} />
              </p>
              {hitEnterBadge}
            </div>;
        } else {
          const selectedOptionForValue = getSelectedOptionForSearchValue({
            isCaseSensitive,
            searchValue,
            selectedOptions
          });
          if (selectedOptionForValue) {
            // Disallow duplicate custom options.
            emptyStateContent = <p>
                <EuiI18n token="euiComboBoxOptionsList.alreadyAdded" default="{label} has already been added" values={{
                label: <strong>{selectedOptionForValue.label}</strong>
              }} />
              </p>;
          } else {
            const highlightSearchValue = (text, searchValue) => {
              const reg = new RegExp(/(\{searchValue})/, 'gi');
              const parts = text.split(reg);
              return <p className="euiComboBoxOption__emptyStateText">
                  {parts.map((part, idx) => part.match(reg) ? <strong key={idx}>{searchValue}</strong> : part)}
                </p>;
            };
            emptyStateContent = <div className="euiComboBoxOption__contentWrapper">
                {customOptionText ? highlightSearchValue(customOptionText, searchValue) : <p className="euiComboBoxOption__emptyStateText">
                    <EuiI18n token="euiComboBoxOptionsList.createCustomOption" default="Add {searchValue} as a custom option" values={{
                  searchValue: <strong>{searchValue}</strong>
                }} />
                  </p>}
                {hitEnterBadge}
              </div>;
          }
        }
      } else {
        emptyStateContent = <p>
            <EuiI18n token="euiComboBoxOptionsList.noMatchingOptions" default="{searchValue} doesn't match any options" values={{
            searchValue: <strong>{searchValue}</strong>
          }} />
          </p>;
      }
    } else if (!options.length) {
      emptyStateContent = <p>
          <EuiI18n token="euiComboBoxOptionsList.noAvailableOptions" default="There aren't any options available" />
        </p>;
    } else if (areAllOptionsSelected) {
      emptyStateContent = <p>
          <EuiI18n token="euiComboBoxOptionsList.allOptionsSelected" default="You've selected all available options" />
        </p>;
    }
    const emptyState = emptyStateContent ? <EuiText size="xs" className="euiComboBoxOptionsList__empty">
        {emptyStateContent}
      </EuiText> : undefined;
    const numVisibleOptions = matchingOptions.length < 7 ? matchingOptions.length : 7;
    const height = numVisibleOptions * (rowHeight + 1); // Add one for the border

    // bounded by max-height of euiComboBoxOptionsList__rowWrap
    const boundedHeight = height > 200 ? 200 : height;
    const optionsList = <FixedSizeList height={boundedHeight} onScroll={onScroll} itemCount={matchingOptions.length} itemSize={rowHeight} itemData={matchingOptions} ref={this.setListRef} innerRef={this.setListBoxRef} width={width}>
        {this.ListRow}
      </FixedSizeList>;

    /**
     * Reusing the EuiPopover__panel classes to help with consistency/maintenance.
     * But this should really be converted to user the popover component.
     */
    const classes = classNames('euiComboBoxOptionsList');
    return <EuiPopoverPanel paddingSize="none" hasShadow={false} className={classes} panelRef={this.listRefCallback} data-test-subj={classNames('comboBoxOptionsList', dataTestSubj)} style={{
      ...style,
      zIndex: zIndex
    }} isOpen isAttached position={position} {...rest}>
        <div className="euiComboBoxOptionsList__rowWrap">
          {emptyState || optionsList}
        </div>
      </EuiPopoverPanel>;
  }
}
EuiComboBoxOptionsList.propTypes = {
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  css: PropTypes.any,
  activeOptionIndex: PropTypes.number,
  areAllOptionsSelected: PropTypes.bool,
  listboxAriaLabel: PropTypes.string.isRequired,
  /**
       * Creates a custom text option. You can use `{searchValue}` inside your string to better customize your text.
       * It won't show if there's no onCreateOption.
       */
  customOptionText: PropTypes.string,
  fullWidth: PropTypes.bool,
  getSelectedOptionForSearchValue: PropTypes.func,
  isCaseSensitive: PropTypes.bool,
  isLoading: PropTypes.bool,
  listRef: PropTypes.any.isRequired,
  matchingOptions: PropTypes.arrayOf(PropTypes.shape({
    isGroupLabelOption: PropTypes.bool,
    label: PropTypes.string.isRequired,
    key: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.shape({
      isGroupLabelOption: PropTypes.bool,
      label: PropTypes.string.isRequired,
      key: PropTypes.string,
      options: PropTypes.arrayOf(PropTypes.any.isRequired),
      value: PropTypes.any,
      prepend: PropTypes.node,
      append: PropTypes.node,
      truncationProps: PropTypes.any,
      className: PropTypes.string,
      "aria-label": PropTypes.string,
      "data-test-subj": PropTypes.string,
      css: PropTypes.any
    }).isRequired),
    value: PropTypes.any,
    prepend: PropTypes.node,
    append: PropTypes.node,
    truncationProps: PropTypes.any,
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,
    css: PropTypes.any
  }).isRequired).isRequired,
  onCloseList: PropTypes.func.isRequired,
  onCreateOption: PropTypes.func,
  onOptionClick: PropTypes.func,
  onOptionEnterKey: PropTypes.func,
  onScroll: PropTypes.any,
  optionRef: PropTypes.func.isRequired,
  /**
       * Array of EuiComboBoxOptionOption objects. See #EuiComboBoxOptionOption
       */
  options: PropTypes.arrayOf(PropTypes.shape({
    isGroupLabelOption: PropTypes.bool,
    label: PropTypes.string.isRequired,
    key: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.shape({
      isGroupLabelOption: PropTypes.bool,
      label: PropTypes.string.isRequired,
      key: PropTypes.string,
      options: PropTypes.arrayOf(PropTypes.any.isRequired),
      value: PropTypes.any,
      prepend: PropTypes.node,
      append: PropTypes.node,
      truncationProps: PropTypes.any,
      className: PropTypes.string,
      "aria-label": PropTypes.string,
      "data-test-subj": PropTypes.string,
      css: PropTypes.any
    }).isRequired),
    value: PropTypes.any,
    prepend: PropTypes.node,
    append: PropTypes.node,
    truncationProps: PropTypes.any,
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,
    css: PropTypes.any
  }).isRequired).isRequired,
  position: PropTypes.oneOf(["top", "bottom"]),
  renderOption: PropTypes.func,
  rootId: PropTypes.any.isRequired,
  rowHeight: PropTypes.number.isRequired,
  scrollToIndex: PropTypes.number,
  searchValue: PropTypes.string.isRequired,
  selectedOptions: PropTypes.arrayOf(PropTypes.shape({
    isGroupLabelOption: PropTypes.bool,
    label: PropTypes.string.isRequired,
    key: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.shape({
      isGroupLabelOption: PropTypes.bool,
      label: PropTypes.string.isRequired,
      key: PropTypes.string,
      options: PropTypes.arrayOf(PropTypes.any.isRequired),
      value: PropTypes.any,
      prepend: PropTypes.node,
      append: PropTypes.node,
      truncationProps: PropTypes.any,
      className: PropTypes.string,
      "aria-label": PropTypes.string,
      "data-test-subj": PropTypes.string,
      css: PropTypes.any
    }).isRequired),
    value: PropTypes.any,
    prepend: PropTypes.node,
    append: PropTypes.node,
    truncationProps: PropTypes.any,
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,
    css: PropTypes.any
  }).isRequired).isRequired,
  updatePosition: PropTypes.func.isRequired,
  width: PropTypes.number.isRequired,
  singleSelection: PropTypes.oneOfType([PropTypes.bool.isRequired, PropTypes.shape({
    asPlainText: PropTypes.bool
  }).isRequired]),
  delimiter: PropTypes.string,
  zIndex: PropTypes.number,
  truncationProps: PropTypes.any
};
try {
  EuiComboBoxOptionsList.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/combo_box/combo_box_options_list/combo_box_options_list.tsx',
    description: '',
    displayName: 'EuiComboBoxOptionsList',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: {
          value: ''
        },
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      css: {
        defaultValue: null,
        description: '',
        name: 'css',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'Interpolation<Theme>'
        }
      },
      element: {
        defaultValue: null,
        description: '',
        name: 'element',
        parent: {
          fileName: 'app/src/components/panel/panel.tsx',
          name: '_EuiPanelDivlike'
        },
        declarations: [{
          fileName: 'app/src/components/panel/panel.tsx',
          name: '_EuiPanelDivlike'
        }],
        required: false,
        type: {
          name: '"div"'
        }
      },
      hasShadow: {
        defaultValue: null,
        description: 'Adds a medium shadow to the panel;\nOnly works when `color="plain"`',
        name: 'hasShadow',
        parent: {
          fileName: 'app/src/components/panel/panel.tsx',
          name: '_EuiPanelProps'
        },
        declarations: [{
          fileName: 'app/src/components/panel/panel.tsx',
          name: '_EuiPanelProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      hasBorder: {
        defaultValue: null,
        description: 'Adds a slight 1px border on all edges.\n' + 'Only works when `color="plain | transparent"`',
        name: 'hasBorder',
        parent: {
          fileName: 'app/src/components/panel/panel.tsx',
          name: '_EuiPanelProps'
        },
        declarations: [{
          fileName: 'app/src/components/panel/panel.tsx',
          name: '_EuiPanelProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      paddingSize: {
        defaultValue: null,
        description: 'Padding for all four sides',
        name: 'paddingSize',
        parent: {
          fileName: 'app/src/components/panel/panel.tsx',
          name: '_EuiPanelProps'
        },
        declarations: [{
          fileName: 'app/src/components/panel/panel.tsx',
          name: '_EuiPanelProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"none" | "s" | "m" | "xs" | "l" | "xl"',
          value: [{
            value: '"none"'
          }, {
            value: '"s"'
          }, {
            value: '"m"'
          }, {
            value: '"xs"'
          }, {
            value: '"l"'
          }, {
            value: '"xl"'
          }]
        }
      },
      borderRadius: {
        defaultValue: null,
        description: 'Corner border radius',
        name: 'borderRadius',
        parent: {
          fileName: 'app/src/components/panel/panel.tsx',
          name: '_EuiPanelProps'
        },
        declarations: [{
          fileName: 'app/src/components/panel/panel.tsx',
          name: '_EuiPanelProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"none" | "m"',
          value: [{
            value: '"none"'
          }, {
            value: '"m"'
          }]
        }
      },
      grow: {
        defaultValue: null,
        description: 'When true the panel will grow in height to match `EuiFlexItem`',
        name: 'grow',
        parent: {
          fileName: 'app/src/components/panel/panel.tsx',
          name: '_EuiPanelProps'
        },
        declarations: [{
          fileName: 'app/src/components/panel/panel.tsx',
          name: '_EuiPanelProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      panelRef: {
        defaultValue: null,
        description: '',
        name: 'panelRef',
        parent: {
          fileName: 'app/src/components/panel/panel.tsx',
          name: '_EuiPanelProps'
        },
        declarations: [{
          fileName: 'app/src/components/panel/panel.tsx',
          name: '_EuiPanelProps'
        }],
        required: false,
        type: {
          name: 'Ref<HTMLDivElement>'
        }
      },
      color: {
        defaultValue: null,
        description: 'Background color of the panel;\n' + 'Usually a lightened form of the brand colors',
        name: 'color',
        parent: {
          fileName: 'app/src/components/panel/panel.tsx',
          name: '_EuiPanelProps'
        },
        declarations: [{
          fileName: 'app/src/components/panel/panel.tsx',
          name: '_EuiPanelProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"transparent" | "accent" | "primary" | "success" | "warning" | "danger" | "subdued" | "plain"',
          value: [{
            value: '"transparent"'
          }, {
            value: '"accent"'
          }, {
            value: '"primary"'
          }, {
            value: '"success"'
          }, {
            value: '"warning"'
          }, {
            value: '"danger"'
          }, {
            value: '"subdued"'
          }, {
            value: '"plain"'
          }]
        }
      },
      isOpen: {
        defaultValue: null,
        description: '',
        name: 'isOpen',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/popover/popover_panel/_popover_panel.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isAttached: {
        defaultValue: null,
        description: '',
        name: 'isAttached',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/popover/popover_panel/_popover_panel.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      position: {
        defaultValue: null,
        description: '',
        name: 'position',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/popover/popover_panel/_popover_panel.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/combo_box/combo_box_options_list/combo_box_options_list.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"top" | "bottom"',
          value: [{
            value: '"top"'
          }, {
            value: '"bottom"'
          }]
        }
      },
      hasDragDrop: {
        defaultValue: null,
        description: '',
        name: 'hasDragDrop',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/popover/popover_panel/_popover_panel.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      activeOptionIndex: {
        defaultValue: null,
        description: '',
        name: 'activeOptionIndex',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_options_list/combo_box_options_list.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'number'
        }
      },
      areAllOptionsSelected: {
        defaultValue: null,
        description: '',
        name: 'areAllOptionsSelected',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_options_list/combo_box_options_list.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      listboxAriaLabel: {
        defaultValue: null,
        description: '',
        name: 'listboxAriaLabel',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_options_list/combo_box_options_list.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'string'
        }
      },
      customOptionText: {
        defaultValue: null,
        description: 'Creates a custom text option. You can use `{searchValue}` inside your string to better customize your text.\n' + "It won't show if there's no onCreateOption.",
        name: 'customOptionText',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_options_list/combo_box_options_list.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      fullWidth: {
        defaultValue: null,
        description: '',
        name: 'fullWidth',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_options_list/combo_box_options_list.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      getSelectedOptionForSearchValue: {
        defaultValue: null,
        description: '',
        name: 'getSelectedOptionForSearchValue',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_options_list/combo_box_options_list.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: '(params: { isCaseSensitive?: boolean; searchValue: string; selectedOptions: any[]; }) => EuiComboBoxOptionOption<T>'
        }
      },
      isCaseSensitive: {
        defaultValue: {
          value: 'false'
        },
        description: '',
        name: 'isCaseSensitive',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_options_list/combo_box_options_list.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isLoading: {
        defaultValue: null,
        description: '',
        name: 'isLoading',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_options_list/combo_box_options_list.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      listRef: {
        defaultValue: null,
        description: '',
        name: 'listRef',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_options_list/combo_box_options_list.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: '(instance: HTMLDivElement) => void'
        }
      },
      matchingOptions: {
        defaultValue: null,
        description: '',
        name: 'matchingOptions',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_options_list/combo_box_options_list.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'EuiComboBoxOptionOption<T>[]'
        }
      },
      onCloseList: {
        defaultValue: null,
        description: '',
        name: 'onCloseList',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_options_list/combo_box_options_list.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: '(event: Event) => void'
        }
      },
      onCreateOption: {
        defaultValue: null,
        description: '',
        name: 'onCreateOption',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_options_list/combo_box_options_list.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: '(searchValue: string, options: EuiComboBoxOptionOption<T>[]) => boolean | void'
        }
      },
      onOptionClick: {
        defaultValue: null,
        description: '',
        name: 'onOptionClick',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_options_list/combo_box_options_list.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'OptionHandler<T>'
        }
      },
      onOptionEnterKey: {
        defaultValue: null,
        description: '',
        name: 'onOptionEnterKey',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_options_list/combo_box_options_list.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'OptionHandler<T>'
        }
      },
      optionRef: {
        defaultValue: null,
        description: '',
        name: 'optionRef',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_options_list/combo_box_options_list.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: '(index: number, node: EuiFilterSelectItemClass) => void'
        }
      },
      options: {
        defaultValue: null,
        description: 'Array of EuiComboBoxOptionOption objects. See #EuiComboBoxOptionOption',
        name: 'options',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_options_list/combo_box_options_list.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'EuiComboBoxOptionOption<T>[]'
        }
      },
      renderOption: {
        defaultValue: null,
        description: '',
        name: 'renderOption',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_options_list/combo_box_options_list.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: '(option: EuiComboBoxOptionOption<T>, searchValue: string, OPTION_CONTENT_CLASSNAME: string) => ReactNode'
        }
      },
      rootId: {
        defaultValue: null,
        description: '',
        name: 'rootId',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_options_list/combo_box_options_list.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: '(idSuffix?: string) => string'
        }
      },
      rowHeight: {
        defaultValue: {
          value: '29'
        },
        description: '',
        name: 'rowHeight',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_options_list/combo_box_options_list.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'number'
        }
      },
      scrollToIndex: {
        defaultValue: null,
        description: '',
        name: 'scrollToIndex',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_options_list/combo_box_options_list.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'number'
        }
      },
      searchValue: {
        defaultValue: null,
        description: '',
        name: 'searchValue',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_options_list/combo_box_options_list.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'string'
        }
      },
      selectedOptions: {
        defaultValue: null,
        description: '',
        name: 'selectedOptions',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_options_list/combo_box_options_list.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'EuiComboBoxOptionOption<T>[]'
        }
      },
      updatePosition: {
        defaultValue: null,
        description: '',
        name: 'updatePosition',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_options_list/combo_box_options_list.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'UpdatePositionHandler'
        }
      },
      width: {
        defaultValue: null,
        description: '',
        name: 'width',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_options_list/combo_box_options_list.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'number'
        }
      },
      singleSelection: {
        defaultValue: null,
        description: '',
        name: 'singleSelection',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_options_list/combo_box_options_list.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean | EuiComboBoxSingleSelectionShape'
        }
      },
      delimiter: {
        defaultValue: null,
        description: '',
        name: 'delimiter',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_options_list/combo_box_options_list.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      zIndex: {
        defaultValue: null,
        description: '',
        name: 'zIndex',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_options_list/combo_box_options_list.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'number'
        }
      },
      truncationProps: {
        defaultValue: null,
        description: '',
        name: 'truncationProps',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_options_list/combo_box_options_list.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'Partial<Omit<EuiTextTruncateProps, "children" | "text">>'
        }
      }
    },
    extendedInterfaces: ['CommonProps', '_EuiPanelDivlike', '_EuiPanelProps', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes']
  };
} catch (e) {}